import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import {
    getStripeCustomerQueryKey,
    getStripeProductsQueryKey
} from '@/utils/query-keys';
import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult
} from '@tanstack/react-query';
import Stripe from 'stripe';

interface StripeCustomerResponse {
  subscription: Stripe.Subscription | null;
  customer: Stripe.Customer;
  product: Stripe.Product | null;
  trialEligible: boolean;
}

interface CheckoutSessionResponse {
  url?: string;
  message?: string;
}

interface PortalSessionResponse {
  url: string;
}

export interface ProductWithPricing extends Stripe.Product {
  prices: Stripe.Price[];
}

interface useStripeResponse {
  stripeCustomer: UseQueryResult<StripeCustomerResponse, Error>;
  products: UseQueryResult<ProductWithPricing[], Error>;
  createCheckoutSession: UseMutationResult<
    CheckoutSessionResponse,
    Error,
    {
      priceId: string;
      quantity: number;
      returnUrl: string;
      trialPeriodDays?: number;
      coupon?: string;
    }
  >;
  createPortalSession: UseMutationResult<PortalSessionResponse, Error, string>;
  upgradeTeamMembers: UseMutationResult<
    CheckoutSessionResponse,
    Error,
    {
      additional_members: number;
      return_url: string;
    }
  >;
}

export function useStripe(): useStripeResponse {
  const stripeCustomer = useQuery({
    queryKey: getStripeCustomerQueryKey(),
    queryFn: async () => {
      const response = await callApi(supabase, '/stripe/customer', {
        method: 'GET',
      });

      if (response.error) {
        throw response.error;
      }

      return response.data as StripeCustomerResponse;
    },
  });

  const products = useQuery({
    queryKey: getStripeProductsQueryKey(),
    queryFn: async () => {
      const response = await callApi(supabase, '/products', {
        method: 'GET',
      });

      if (response.error) {
        throw response.error;
      }

      return response.data as ProductWithPricing[];
    },
  });

  const createCheckoutSession = useMutation({
    mutationFn: async ({
      priceId,
      quantity,
      returnUrl,
      trialPeriodDays,
      coupon,
    }: {
      priceId: string;
      quantity: number;
      returnUrl: string;
      trialPeriodDays?: number;
      coupon?: string;
    }) => {
      const response = await callApi(supabase, '/stripe/checkout', {
        method: 'POST',
        data: {
          priceId,
          quantity,
          return_url: returnUrl,
          trial_period_days: trialPeriodDays || 0,
          ...(coupon ? { coupon } : {}),
        },
      });

      if (response.error) {
        throw response.error;
      }

      return response.data as CheckoutSessionResponse;
    },
    onError: (error) => {
      console.error('Error creating checkout session:', error);
    },
  });

  const createPortalSession = useMutation({
    mutationFn: async (returnUrl: string) => {
      const response = await callApi(supabase, '/stripe/portal', {
        method: 'POST',
        data: {
          return_url: returnUrl,
        },
      });

      if (response.error) {
        throw response.error;
      }

      return response.data as PortalSessionResponse;
    },
    onError: (error) => {
      console.error('Error creating portal session:', error);
    },
  });

  const upgradeTeamMembers = useMutation({
    mutationFn: async ({
      additional_members,
      return_url,
    }: {
      additional_members: number;
      return_url: string;
    }) => {
      const response = await callApi(supabase, '/stripe/upgrade-team-members', {
        method: 'POST',
        data: {
          additional_members,
          return_url,
        },
      });

      if (response.error) {
        throw response.error;
      }

      return response.data as CheckoutSessionResponse;
    },
    onError: (error) => {
      console.error('Error upgrading team members:', error);
    },
  });

  return {
    stripeCustomer,
    products,
    createCheckoutSession,
    createPortalSession,
    upgradeTeamMembers,
  };
}
